import React, { useState } from 'react';
import ShareOptionsModal from './ShareOptionsModal';
import UserSearchModal from './UserSearchModal';
import { ShareService } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';

/* INCOMING CARDS COMPONENT - Blue card design for incoming events */
/* Used on Home page in the "Incoming" section for new events */
/* Shows event details with Accept/Decline/Edit buttons */
const IncomingCards = ({ event, onAccept, onIgnore, onEdit, getCategoryStyles, formatDateTime }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const { currentUser } = useAuth();
  
  /* RESPONSIVE LISTENER - Adjusts layout for mobile screens */
  /* Updates icon sizes, text sizes, and spacing */
  React.useEffect(() => {
    const handleResize = () => {
      // Update mobile state
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
    };
    
    // Initial mobile check
    setIsMobile(window.innerWidth <= 768);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ACCEPT HANDLER - Logs event details and accepts event */
  /* Used for debugging and tracking event source */
  const handleAccept = (e) => {
    if (e) e.stopPropagation();
    console.log('🔵 Accepting event with ID:', event.id);
    console.log('🔵 Event source:', event.source);
    console.log('🔵 Full event object:', event);
    onAccept(event.id);
  };
  
  // Handle edit event
  const handleEdit = (e) => {
    if (e) e.stopPropagation();
    if (onEdit) {
      onEdit(event);
    }
  };

  /* SHARE HANDLER - Opens share options modal */
  const handleShare = (e) => {
    if (e) e.stopPropagation();
    setShowShareModal(true);
  };

  const handleShareViaWhatsApp = () => {
    // Use the ShareService to share via WhatsApp with signup prompt
    ShareService.shareViaWhatsAppWithSignup(event, formatDateTime, null);
    setShowShareModal(false);
  };

  const handleShareWithFriends = () => {
    setShowShareModal(false);
    setShowUserSearchModal(true);
  };

  const handleUserSelect = async (email) => {
    try {
      const result = await ShareService.shareWithUser(event, email, currentUser.uid);
      if (result.success) {
        alert(result.message);
      } else {
        alert(result.error || 'Failed to share event');
      }
    } catch (error) {
      console.error('Error sharing event:', error);
      alert(error.message || 'Failed to share event. Please try again.');
    }
  };

  const handleIgnore = (e) => {
    if (e) e.stopPropagation();
    console.log('🔶 Ignoring event with ID:', event.id);
    console.log('🔶 Event firestoreId:', event.firestoreId);
    console.log('🔶 Full event object:', event);
    onIgnore(event.id);
  };

  /* CARD COLOR - Light blue gradient background for all incoming cards */
  /* Red gradient for duplicate events */
  const getCardBackground = () => {
    return "#0071c1"; // Lighter blue
  };
  
  // Function to create a lighter version of a color for gradient
  const getLighterColor = (hexColor, percent) => {
    // Convert hex to RGB
    let r = parseInt(hexColor.slice(1, 3), 16);
    let g = parseInt(hexColor.slice(3, 5), 16);
    let b = parseInt(hexColor.slice(5, 7), 16);
    
    // Make it lighter but less intense
    r = Math.min(255, r + Math.floor((255 - r) * (percent / 150)));
    g = Math.min(255, g + Math.floor((255 - g) * (percent / 150)));
    b = Math.min(255, b + Math.floor((255 - b) * (percent / 150)));
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const containerStyle = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '12px',
    marginBottom: '16px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    margin: '0 auto',
    left: '0',
    right: '0',
    display: 'flex',
    justifyContent: 'center'
  };

  const eventStyle = {
    zIndex: 2,
    position: 'relative',
    background: event.isDuplicate 
      ? `radial-gradient(ellipse at bottom right, ${getLighterColor("#E63946", 25)} 0%, #E63946 45%)` 
      : `radial-gradient(ellipse at bottom right, ${getLighterColor(getCardBackground(), 40)} 0%, ${getCardBackground()} 45%)`,
    color: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    boxSizing: 'border-box',
    padding: isMobile ? '16px' : '24px',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    minHeight: isMobile ? '105px' : '140px',
    margin: '0 auto',
    left: '0',
    right: '0',
    overflow: 'hidden'
  };

  const eventIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const eventTitleStyle = {
    fontWeight: '600',
    fontSize: isMobile ? '1.1rem' : '1.25rem',
    marginBottom: isMobile ? '2px' : '4px',
    color: '#ffffff',
    flex: 1
  };

  const eventTimeStyle = {
    fontSize: isMobile ? '0.9rem' : '1rem',
    color: '#ffffff',
    opacity: 0.85,
    fontWeight: '500'
  };

  const actionButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: isMobile ? '5px 10px' : '7px 12px',
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: isMobile ? '0.8rem' : '0.9rem',
    cursor: 'pointer',
    marginTop: '4px',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    minHeight: isMobile ? '32px' : '36px'
  };

  /* ICON STYLING - White icons with consistent sizing */
  /* Applied to category, calendar, location, and people icons */
  const iconStyle = {
    width: "20px",
    height: "20px",
    filter: "brightness(0) invert(1)"
  };
  
  // Fixed consistent spacing for all icons
  const iconSpacing = '8px';
  
  // Left padding for content alignment
  const contentPadding = '28px';

  /* INCOMING CARD UI - Blue gradient card with aligned content */
  /* Icons on left, content in middle, share button on right */
  return (
    <>
      <div style={containerStyle}>
      <div 
        className="event-item"
        style={eventStyle}
      >
        <div style={{width: '100%'}}>
          <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px', width: '100%'}}>
            <div style={{width: contentPadding, display: 'flex', justifyContent: 'flex-start'}}>
              <div style={eventIconStyle}>
                {event.category === 'work' && (
                  <img
                    src="/briefcase.svg"
                    alt="Work"
                    style={iconStyle}
                  />
                )}
                {event.category === 'school' && (
                  <img
                    src="/graduation-cap.svg"
                    alt="School"
                    style={iconStyle}
                  />
                )}
                {event.category === 'class' && (
                  <img
                    src="/group.png"
                    alt="Class"
                    style={iconStyle}
                  />
                )}
                {event.category === 'sport' && (
                  <img
                    src="/tennis.svg"
                    alt="Sport"
                    style={iconStyle}
                  />
                )}
                {event.category === 'life' && (
                  <img
                    src="/circle-star.svg"
                    alt="Life"
                    style={iconStyle}
                  />
                )}
                {(!event.category || event.category === 'other') && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                )}
              </div>
            </div>
            <div style={{...eventTitleStyle, marginLeft: 0, marginBottom: 0}}>
              {event.eventName}
              {event.isDuplicate && (
                <span style={{
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                  color: '#ffffff',
                  padding: '2px 5px',
                  borderRadius: '3px',
                  marginLeft: '8px',
                  verticalAlign: 'middle',
                  display: 'inline-block'
                }}>
                  DUPLICATE
                </span>
              )}
            </div>
            <button
              className="action-button share-button"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 10,
                minWidth: '32px',
                minHeight: '32px'
              }}
              onClick={handleShare}
              title="Share Event"
            >
              {/* Share icon */}
              <img
                src="/share.png"
                alt="Share"
                style={iconStyle}
              />
            </button>
          </div>
          <div style={{...eventTimeStyle, marginTop: isMobile ? '4px' : '6px', width: '100%', marginBottom: isMobile ? '10px' : '12px', display: 'flex', alignItems: 'center'}}>
            <div style={{width: contentPadding, display: 'flex', justifyContent: 'flex-start'}}>
              <img 
                src="/calendar.png" 
                alt="Date" 
                style={{...iconStyle}}
              />
            </div>
            {formatDateTime(event)}
          </div>
          
          <div style={{width: '100%'}}>
            {event.description && (
              <div style={{
                marginBottom: isMobile ? '6px' : '8px', 
                fontSize: isMobile ? '0.8rem' : '0.9rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                width: '100%',
                paddingLeft: `calc(${contentPadding} + 2px)`,
                display: 'flex',
                alignItems: 'center'
              }}>
                {event.description}
              </div>
            )}
            
            {event.location && (
              <div style={{
                marginBottom: isMobile ? '6px' : '8px', 
                fontSize: isMobile ? '0.8rem' : '0.9rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%'
              }}>
                <div style={{width: contentPadding, display: 'flex', justifyContent: 'flex-start'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                {event.location}
              </div>
            )}
            
            {event.participant && (
              <div style={{
                marginBottom: isMobile ? '6px' : '8px', 
                fontSize: isMobile ? '0.8rem' : '0.9rem', 
                width: '100%'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '3px'
                }}>
                  <div style={{width: contentPadding, display: 'flex', justifyContent: 'flex-start'}}>
                    <img 
                      src="/people.png" 
                      alt="Participants" 
                      style={{...iconStyle}}
                    />
                  </div>
                
                  {event.participants && Array.isArray(event.participants)
                    ? event.participants.map((personEmail, index) => {
                        // Check if we have participantNames field with actual names
                        const displayName = event.participantNames && event.participantNames[index] 
                          ? event.participantNames[index] 
                          : personEmail.includes('@') 
                            ? personEmail.split('@')[0] // If it's an email, show username part
                            : personEmail; // Otherwise show as is
                        
                        return (
                          <div key={index} style={{
                            backgroundColor: 'rgba(0, 80, 140, 0.7)',
                            color: '#ffffff',
                            borderRadius: '14px',
                            padding: '3px 8px',
                            fontSize: isMobile ? '0.75rem' : '0.85rem',
                            display: 'inline-block'
                          }}>
                            {displayName}
                          </div>
                        );
                      })
                    : event.participant.split(',').map((personEmail, index) => {
                        const trimmedPerson = personEmail.trim();
                        // Check if we have participantNames field with actual names
                        const displayName = event.participantNames && event.participantNames[index] 
                          ? event.participantNames[index] 
                          : trimmedPerson.includes('@') 
                            ? trimmedPerson.split('@')[0] // If it's an email, show username part
                            : trimmedPerson; // Otherwise show as is
                        
                        return (
                          <div key={index} style={{
                            backgroundColor: 'rgba(0, 80, 140, 0.7)',
                            color: '#ffffff',
                            borderRadius: '14px',
                            padding: '3px 8px',
                            fontSize: isMobile ? '0.75rem' : '0.85rem',
                            display: 'inline-block'
                          }}>
                            {displayName}
                          </div>
                        );
                      })
                  }
                </div>
              </div>
            )}
            
            {/* ACTION BUTTONS - Bottom right corner buttons */}
            {/* Accept/Decline/Edit with transparent background */}
            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: isMobile ? '6px' : '10px', gap: isMobile ? '12px' : '16px', width: '100%'}}>
              <button
                className="action-button accept-button"
                style={{
                  ...actionButtonStyle
                }}
                onClick={handleAccept}
              >
                + Accept
              </button>
              <button
                className="action-button ignore-button"
                style={{
                  ...actionButtonStyle
                }}
                onClick={handleIgnore}
              >
                + Decline
              </button>
              <button
                className="action-button edit-button"
                style={{
                  ...actionButtonStyle
                }}
                onClick={handleEdit}
              >
                + Edit
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Share Options Modal */}
      <ShareOptionsModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onSelectOption={(option) => {
          if (option === 'whatsapp') {
            handleShareViaWhatsApp();
          } else if (option === 'signup') {
            handleShareViaWhatsApp(); // Using the same function for WhatsApp signup
          } else if (option === 'user') {
            handleShareWithFriends();
          }
        }}
        event={event}
        formatDateTime={formatDateTime}
      />

      {/* User Search Modal */}
      <UserSearchModal
        isOpen={showUserSearchModal}
        onClose={() => setShowUserSearchModal(false)}
        onSelectUser={handleUserSelect}
      />
    </>
  );
};

export default IncomingCards;